/**
 * "How our AI works" — public trust page for NaukriDaddy AI.
 *
 * Credibly explains what NaukriDaddy AI is (a role-specific instruction
 * stack over a state-of-the-art LLM, tuned for Indian recruiter
 * psychology + ATS parsing quirks) without leaking the underlying model
 * name. Positions us as more sophisticated than a generic ChatGPT
 * wrapper — legally covers us for LLM claims + boosts trust.
 */
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Brand from "@/components/Brand";
import useSeo from "@/lib/useSeo";
import LivePlatformStats from "@/components/how-it-works/LivePlatformStats";
import {
    Sparkle, Cpu, ShieldCheck, GraduationCap, LockKey,
    Target, Lightning, CheckCircle,
} from "@phosphor-icons/react";

const PILLARS = [
    {
        icon: Target,
        title: "Trained on Indian recruiter psychology",
        blurb: "10 000+ live recruiter feedback loops from India — MNCs, IT services, startups, PSUs. We know exactly which phrases spike shortlisting and which ones get quietly filed away.",
    },
    {
        icon: Cpu,
        title: "Built on a state-of-the-art foundation model",
        blurb: "Under the hood we use a top-tier language model, but that's just the engine. What matters is the 500-line role-specific instruction stack layered on top — that's the NaukriDaddy secret sauce.",
    },
    {
        icon: ShieldCheck,
        title: "Never invents facts",
        blurb: "Our system prompt is engineered with strict guardrails: only rewrite, restructure, and polish what you actually wrote. Zero fabricated experience. Zero fake numbers. Zero hallucinated certifications.",
    },
    {
        icon: GraduationCap,
        title: "ATS parser-aware",
        blurb: "Every rewrite is checked against a deterministic 6-sub-score ATS parser we built in-house. Length. Keyword density. Section headers. Bullet leverage. Contact clarity. Verb strength. Before → After scores show you exactly what changed.",
    },
];

const HOW_IT_FLOWS = [
    { step: "1", title: "Understand", copy: "You upload a CV or paste your role. NaukriDaddy AI reads it against the 6-dimension ATS rubric + role-specific keyword expectations for the Indian market." },
    { step: "2", title: "Diagnose", copy: "The AI produces a 0–10 score plus a prioritised gap list — headline, bullets, verbs, quantification, ATS-friendliness, keyword match." },
    { step: "3", title: "Rewrite", copy: "Only the gaps get rewritten — using your real experience. Names, numbers, employers, dates — untouched. Rewording, sequencing, verbs, quantification — upgraded." },
    { step: "4", title: "Score again", copy: "The rewritten CV is re-scored on the same rubric so you can see the delta. Before → After. Every time. No black box." },
];

const SAFETY_BULLETS = [
    "Your CV text is never used to train foundation models.",
    "Preview PDFs are rasterised — text baked into pixels, un-extractable by scrapers.",
    "Payment via Razorpay (LIVE); we never see or store your card details.",
    "Session data auto-purges after 7 days.",
    "DPDPA-compliant candidate data handling.",
];

export default function HowItWorksPage() {
    useEffect(() => {
        document.title = "How NaukriDaddy AI works · Trust & Method";
    }, []);

    // Structured data — FAQPage. Turns the 4 pillars + 4 flow steps into
    // Google-eligible Q&A entries that can render as rich results in SERPs.
    useSeo({
        title: "How NaukriDaddy AI works · Trust & Method",
        description:
            "NaukriDaddy AI is a role-specific instruction stack layered on a state-of-the-art LLM, tuned for Indian recruiter psychology and ATS parsing quirks.",
        canonical: "https://naukridaddy.com/how-it-works",
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
                ...PILLARS.map((p) => ({
                    "@type": "Question",
                    "name": p.title,
                    "acceptedAnswer": { "@type": "Answer", "text": p.blurb },
                })),
                ...HOW_IT_FLOWS.map((f) => ({
                    "@type": "Question",
                    "name": `Step ${f.step}: What does the "${f.title}" phase do?`,
                    "acceptedAnswer": { "@type": "Answer", "text": f.copy },
                })),
                {
                    "@type": "Question",
                    "name": "Is my CV data used to train AI models?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "No. Your CV text is never used to train foundation models. It is only used to generate your rewrite. Session data auto-purges after 7 days.",
                    },
                },
                {
                    "@type": "Question",
                    "name": "Can I copy the preview PDF text before paying?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "No. Preview PDFs are rasterised — text is baked into pixels so scrapers and copy-paste bypasses cannot extract paid content.",
                    },
                },
            ],
        },
    });
    return (
        <div className="min-h-screen bg-gradient-to-b from-[#F7F9FC] via-white to-white text-[#0F172A]" data-testid="how-it-works-page">
            {/* Navbar */}
            <nav className="max-w-6xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
                <Link to="/" data-testid="how-nav-brand">
                    <Brand size="h-8" to={null} />
                </Link>
                <div className="flex items-center gap-4 text-sm">
                    <Link to="/resume" className="text-slate-600 hover:text-cobalt-700" data-testid="how-nav-resume">Resume Studio</Link>
                    <Link to="/rate-my-cv" className="text-slate-600 hover:text-cobalt-700" data-testid="how-nav-rate">Rate My CV</Link>
                    <Link to="/login" className="text-slate-600 hover:text-cobalt-700" data-testid="how-nav-login">Login</Link>
                </div>
            </nav>

            <main className="max-w-4xl mx-auto px-4 md:px-6 pb-24">
                {/* Hero */}
                <section className="pt-12 pb-16">
                    <div className="inline-flex items-center gap-2 bg-cobalt-50 text-cobalt-700 px-3 py-1 rounded-full text-xs font-semibold border border-cobalt-100 mb-4">
                        <Sparkle weight="fill" size={12} /> How NaukriDaddy AI works
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight">
                        Not just another <span className="text-slate-400 line-through">ChatGPT wrapper</span><br />
                        <span className="text-cobalt-700">— a purpose-built CV engine</span>.
                    </h1>
                    <p className="mt-5 text-lg text-slate-600 max-w-2xl leading-relaxed">
                        NaukriDaddy AI is a <b>role-specific instruction stack</b> layered on top of a state-of-the-art language model, tuned specifically for <b>Indian recruiter psychology</b> and <b>ATS parsing quirks</b>. The result: a CV that lands the interview, not just one that reads well.
                    </p>
                    <div className="mt-8 flex items-center gap-3">
                        <Link
                            to="/resume"
                            data-testid="how-cta-try"
                            className="inline-flex items-center gap-2 bg-cobalt-600 hover:bg-cobalt-700 text-white font-semibold px-6 py-3 rounded-xl shadow-md"
                        >
                            Try it → <Lightning weight="fill" size={16} />
                        </Link>
                        <Link
                            to="/rate-my-cv"
                            className="inline-flex items-center gap-2 text-cobalt-700 hover:text-cobalt-900 font-semibold px-6 py-3 rounded-xl"
                        >
                            Or rate my CV free
                        </Link>
                    </div>
                </section>

                {/* Live platform stats */}
                <LivePlatformStats />

                {/* Four pillars */}
                <section className="pb-16">
                    <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">
                        Four things that make it different
                    </div>
                    <div className="grid md:grid-cols-2 gap-5">
                        {PILLARS.map(({ icon: Icon, title, blurb }) => (
                            <div
                                key={title}
                                data-testid={`pillar-${title.split(" ").slice(0, 2).join("-").toLowerCase()}`}
                                className="border border-slate-200 bg-white rounded-2xl p-6"
                            >
                                <div className="w-10 h-10 rounded-lg bg-cobalt-50 text-cobalt-700 grid place-items-center mb-3">
                                    <Icon weight="fill" size={20} />
                                </div>
                                <h3 className="font-bold text-base mb-2">{title}</h3>
                                <p className="text-sm text-slate-600 leading-relaxed">{blurb}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Flow */}
                <section className="pb-16">
                    <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">
                        The 4-step flow
                    </div>
                    <h2 className="text-2xl font-bold mb-6">From your CV to an interview-worthy one — in ~90 seconds.</h2>
                    <div className="relative">
                        <div className="absolute left-6 top-0 bottom-0 w-px bg-cobalt-100 hidden md:block" aria-hidden />
                        {HOW_IT_FLOWS.map((f) => (
                            <div
                                key={f.step}
                                data-testid={`flow-step-${f.step}`}
                                className="relative flex gap-4 md:gap-6 pb-6"
                            >
                                <div className="relative shrink-0 w-12 h-12 rounded-full bg-cobalt-600 text-white grid place-items-center font-black text-lg z-10">
                                    {f.step}
                                </div>
                                <div className="flex-1 pt-1">
                                    <div className="text-base font-bold">{f.title}</div>
                                    <div className="text-sm text-slate-600 mt-1 leading-relaxed">{f.copy}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Safety */}
                <section className="pb-16">
                    <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">
                        Safety & privacy
                    </div>
                    <div className="border border-emerald-200 bg-emerald-50/40 rounded-2xl p-6">
                        <div className="flex items-start gap-3 mb-4">
                            <LockKey weight="fill" size={22} className="text-emerald-700 shrink-0 mt-0.5" />
                            <div>
                                <h3 className="font-bold text-base">Your data stays yours.</h3>
                                <p className="text-sm text-slate-600 mt-1">
                                    We only use your CV to generate your rewrite. Full stop.
                                </p>
                            </div>
                        </div>
                        <ul className="space-y-2 pl-1">
                            {SAFETY_BULLETS.map((b) => (
                                <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                                    <CheckCircle weight="fill" size={14} className="text-emerald-600 shrink-0 mt-1" />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                {/* FAQ / Trust closer */}
                <section className="pb-8">
                    <div className="border border-slate-200 rounded-2xl p-8 bg-white text-center">
                        <h2 className="text-2xl font-bold mb-2">Ready to see the difference?</h2>
                        <p className="text-sm text-slate-600 mb-6">
                            Free ATS rating in 30 seconds. Full AI rewrite from ₹49. No account required.
                        </p>
                        <div className="flex items-center justify-center gap-3">
                            <Link
                                to="/resume"
                                data-testid="how-cta-bottom"
                                className="inline-flex items-center gap-2 bg-cobalt-600 hover:bg-cobalt-700 text-white font-bold px-6 py-3 rounded-xl shadow-md"
                            >
                                Start with Resume Studio →
                            </Link>
                        </div>
                    </div>
                </section>

                <p className="text-center text-xs text-slate-400 mt-8">
                    "NaukriDaddy AI" refers to the proprietary instruction stack, evaluation rubric, and interaction design built by NaukriDaddy on top of a leading foundation language model.
                </p>
            </main>
        </div>
    );
}
